/**
 * Data Source Manager — Add Layer dialog
 * New vector · Raster · GeoJSON URL · File import
 */
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { AddLocation, CloudUpload, Link, Terrain } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  createLayerAsync,
  createRasterLayerAsync,
  addLayer,
  setActiveLayer,
  addFeatureAsync,
} from '../store/slices/layersSlice';
import { addNotification } from '../store/slices/uiSlice';
import FileUpload, { type SelectedFile } from './import/FileUpload';
import { importFile } from '../services/importService';
import { mapNavigator } from '../services/MapNavigator';
import { normalizeFeaturesForMap } from '../utils/geojsonToVectorLayer';

interface DataSourceManagerProps {
  open: boolean;
  onClose: () => void;
  onImportClick?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} style={{ paddingTop: 12 }}>
    {value === index && children}
  </div>
);

function geomFromType(type?: string): 'Point' | 'LineString' | 'Polygon' {
  if (!type) return 'Polygon';
  if (type === 'GeometryCollection') return 'Polygon';
  if (type.includes('Point')) return 'Point';
  if (type.includes('Line')) return 'LineString';
  return 'Polygon';
}

/** Flatten GeometryCollections + drop unrenderable features at import time */
function featuresForLayer(raw: any[], layerId: string) {
  const normalized = normalizeFeaturesForMap(
    raw.map((f: any, i: number) => ({
      id: f.id ?? `f-${i}`,
      layerId,
      geometry: f.geometry,
      properties: f.properties || {},
    }))
  );
  return normalized.map((f, i) => ({
    id: f.id ?? `${layerId}-f${i}`,
    layerId,
    geometry: f.geometry,
    properties: f.properties || {},
  }));
}

const DataSourceManager: React.FC<DataSourceManagerProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const layers = useAppSelector((s) => s.layers.layers);
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [vectorName, setVectorName] = useState('New Layer');
  const [geometryType, setGeometryType] = useState<'Point' | 'LineString' | 'Polygon'>('Point');

  const [rasterName, setRasterName] = useState('Raster Layer');
  const [rasterSourceType, setRasterSourceType] = useState<'cog' | 'xyz' | 'wms'>('cog');
  const [rasterUrl, setRasterUrl] = useState('');

  const [urlName, setUrlName] = useState('Remote Layer');
  const [url, setUrl] = useState('');

  const handleClose = useCallback(() => {
    setError(null);
    setBusy(false);
    onClose();
  }, [onClose]);

  const handleCreateVector = async () => {
    setBusy(true);
    setError(null);
    try {
      await dispatch(
        createLayerAsync({
          name: vectorName || 'Untitled Layer',
          geometryType,
          crs: 'EPSG:4326',
        })
      ).unwrap();
      dispatch(
        addNotification({ type: 'success', message: 'Layer created — use Edit to digitize' })
      );
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const handleAddRaster = async () => {
    if (!rasterUrl.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await dispatch(
        createRasterLayerAsync({
          name: rasterName || 'Raster Layer',
          sourceType: rasterSourceType,
          sourceUrl: rasterUrl.trim(),
          crs: 'EPSG:3857',
          opacity: 0.85,
        })
      ).unwrap();
      dispatch(addNotification({ type: 'success', message: 'Raster layer added' }));
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Raster add failed');
    } finally {
      setBusy(false);
    }
  };

  const handleAddFromUrl = async () => {
    if (!url.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(url.trim());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const collection =
        json.type === 'FeatureCollection'
          ? json
          : json.type === 'Feature'
            ? { type: 'FeatureCollection', features: [json] }
            : null;
      if (!collection?.features?.length) {
        throw new Error('URL did not return a GeoJSON Feature or FeatureCollection');
      }
      const id = `url-${Date.now()}`;
      const features = featuresForLayer(collection.features, id);
      if (!features.length) {
        throw new Error(
          'No renderable geometries in this GeoJSON (empty or unsupported types only)'
        );
      }
      const geometryType = geomFromType(features[0]?.geometry?.type as string | undefined);
      dispatch(
        addLayer({
          id,
          name: urlName || 'Remote Layer',
          type: 'vector',
          geometryType,
          visible: true,
          opacity: 1,
          zIndex: layers.length + 1,
          featureCount: features.length,
          features,
          source: url.trim(),
        })
      );
      dispatch(setActiveLayer(id));
      mapNavigator.zoomToLayer(id, [
        ...layers,
        {
          id,
          name: urlName,
          type: 'vector',
          visible: true,
          opacity: 1,
          zIndex: 1,
          features,
          geometryType,
        },
      ]);
      dispatch(
        addNotification({
          type: 'success',
          message: `Loaded ${features.length} feature(s) from URL`,
        })
      );
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load URL');
    } finally {
      setBusy(false);
    }
  };

  const handleFileImport = async (selected: SelectedFile[]) => {
    setBusy(true);
    setError(null);
    try {
      let lastLayer: {
        id: string;
        name: string;
        type: 'vector';
        geometryType: 'Point' | 'LineString' | 'Polygon';
        visible: boolean;
        opacity: number;
        zIndex: number;
        featureCount: number;
        features: Array<{
          id: string;
          layerId: string;
          geometry: GeoJSON.Geometry;
          properties: Record<string, unknown>;
        }>;
      } | null = null;

      for (const item of selected) {
        if (!item.file || item.error) continue;
        const result = await importFile(item.file);
        const featuresFc = result.featureCollection;
        if (!featuresFc.features?.length) {
          throw new Error(`No features in ${item.file.name}`);
        }
        const name = item.file.name.replace(/\.[^.]+$/, '') || 'Imported layer';

        // Always load onto map immediately (client layer) so tools work without waiting on API
        const layerId = `import-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        // Explode GeometryCollection etc. so map rendering never crashes
        const features = featuresForLayer(featuresFc.features, layerId);
        if (!features.length) {
          throw new Error(
            `${item.file.name}: no renderable geometries (GeometryCollection exploded to empty)`
          );
        }
        const geometryType = geomFromType(features[0]?.geometry?.type);
        const layer = {
          id: layerId,
          name,
          type: 'vector' as const,
          geometryType,
          visible: true,
          opacity: 1,
          zIndex: layers.length + 1,
          featureCount: features.length,
          features,
          source: item.file.name,
        };
        dispatch(addLayer(layer));
        dispatch(setActiveLayer(layerId));
        lastLayer = layer;

        // Best-effort server persist (non-blocking for map)
        void (async () => {
          try {
            const created = await dispatch(
              createLayerAsync({ name, geometryType, crs: 'EPSG:4326' })
            ).unwrap();
            const batch = 40;
            for (let i = 0; i < featuresFc.features.length; i += batch) {
              const slice = featuresFc.features.slice(i, i + batch);
              await Promise.all(
                slice.map((f: any) =>
                  dispatch(
                    addFeatureAsync({
                      layerId: created.id,
                      geometry: f.geometry,
                      properties: f.properties || {},
                    })
                  )
                )
              );
            }
          } catch {
            /* map already has the data */
          }
        })();

        dispatch(
          addNotification({
            type: 'success',
            message: `Imported ${item.file.name} (${features.length} features)`,
          })
        );
      }

      if (lastLayer) {
        window.setTimeout(() => {
          mapNavigator.zoomToLayer(lastLayer!.id, [...layers, lastLayer!]);
        }, 150);
      }
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
      dispatch(
        addNotification({
          type: 'error',
          message: e instanceof Error ? e.message : 'Import failed',
        })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Add data</DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="New Layer" icon={<AddLocation fontSize="small" />} iconPosition="start" />
          <Tab label="File" icon={<CloudUpload fontSize="small" />} iconPosition="start" />
          <Tab label="From URL" icon={<Link fontSize="small" />} iconPosition="start" />
          <Tab label="Raster" icon={<Terrain fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TabPanel value={tab} index={0}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create an empty editable layer, then use Edit to digitize features.
          </Typography>
          <TextField
            fullWidth
            label="Layer name"
            value={vectorName}
            onChange={(e) => setVectorName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Geometry Type</InputLabel>
            <Select
              value={geometryType}
              label="Geometry Type"
              onChange={(e) => setGeometryType(e.target.value as typeof geometryType)}
            >
              <MenuItem value="Point">Point</MenuItem>
              <MenuItem value="LineString">Line</MenuItem>
              <MenuItem value="Polygon">Polygon</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleCreateVector} fullWidth disabled={busy}>
              {busy ? <CircularProgress size={22} color="inherit" /> : 'Create Layer'}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Import Shapefile (zip), GeoJSON, KML, GPX, or GeoPackage.
          </Typography>
          <FileUpload onImport={handleFileImport} importing={busy} multiple />
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Load a public GeoJSON URL into the map.
          </Typography>
          <TextField
            fullWidth
            label="Layer name"
            value={urlName}
            onChange={(e) => setUrlName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="GeoJSON URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…/data.geojson"
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleAddFromUrl}
            disabled={!url.trim() || busy}
            fullWidth
          >
            {busy ? <CircularProgress size={22} color="inherit" /> : 'Add Layer from URL'}
          </Button>
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a COG, XYZ tiles, or WMS raster.
          </Typography>
          <TextField
            fullWidth
            label="Raster layer name"
            value={rasterName}
            onChange={(e) => setRasterName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Source Type</InputLabel>
            <Select
              value={rasterSourceType}
              label="Source Type"
              onChange={(e) => setRasterSourceType(e.target.value as typeof rasterSourceType)}
            >
              <MenuItem value="cog">Cloud Optimized GeoTIFF (COG)</MenuItem>
              <MenuItem value="xyz">XYZ / TMS Tiles</MenuItem>
              <MenuItem value="wms">WMS</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label={rasterSourceType === 'cog' ? 'COG URL' : 'Tile / WMS URL'}
            value={rasterUrl}
            onChange={(e) => setRasterUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleAddRaster}
            disabled={!rasterUrl.trim() || busy}
            fullWidth
          >
            Add Raster Layer
          </Button>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DataSourceManager;
